import {hasAccountAccess} from '../../functions/permission';
import request from '../../library/request';
import highSecurityRequest from '../../functions/securityRequest/highSecurityRequest';
import store from '../../store';
import setGlobalState from '../setGlobalState';
import securityRequest from '../../functions/securityRequest/securityRequest';
import {getTs} from '../../library/dates';
import {NODE_ENV, REACT_APP_ENV, TICK_DOMAIN_MY} from '../../config/config';
import readCookie from '../../library/readCookie';
import {downloadURI} from '../../component/fileUpload/FileUpload';
import {EVENT_TYPES} from './event.type';
import {uploadFile} from '../user/userActions';
import {generateSVG} from '../../functions/generateSVG';
import {wrapXHR} from '../../functions/wrapXHR';
import i18next from 'i18next';
import {bounceMessage} from '../../layout/userMessages/UserMessage';
import {updateAvenueEid} from '../account/accountActions';
import {AVENUE_QUERY_PARAM} from '../account/account.type';

export const getEid = () => store.getState().event.eid;

const getMapAttributes = (map) => {
    let mapObj = {};
    mapObj.svg = map.areaCount > 0 && map.design ? generateSVG(map.design) : null;
    mapObj = {...mapObj, approveMap:map.approveMap, design:map.design, areas:map.areas, title: map.areaTitle, eid: map.eid, seatCount: map.seatCount, index:0};
    return mapObj;
};
export const getEvent = async (eid) => {
    const res = await request('event.getEvent', 'get', `/event/${eid}`);
    if(res?.type === 'area') {
        res.map = getMapAttributes(res.map || {});
        // NEW-format seatmap: no legacy hall (map.eid is 0), so carry the smid +
        // isNew flag so the app treats the event as "has a map" and opens the
        // studio-based management screen instead of the legacy "connect a hall".
        if(res.useNewSeatmap && res.seatmap?.smid) {
            res.map = {
                ...res.map,
                isNew: true,
                smid: res.seatmap.smid,
                title: res.seatmap.title || res.map.title,
                seatCount: res.seatmap.capacity || res.map.seatCount
            };
        }
        res.maps = [res.map];
    }
    setGlobalState('event', res);
    return res;
};

export const updateEvent = (fields, eid = getEid()) =>
    wrapXHR('event.updateEventDetailsFullXHR', async () => {
        if(!fields?.artists){fields.artists = store.getState().event.artists;}
        if(fields?.fbImgBase64){fields.fbImgBase64 = await uploadFile(fields.fbImgBase64, '', 'hoFbImg', eid);}
        if(fields?.logo){fields.logo = await uploadFile(fields.logo, '', 'hubLogo', eid);}
        const res = await request('event.updateEventDetails', 'put', `/event/${eid}`, fields);
        if(res?.event){
            if(fields.location || fields.locationMap) {
                fields.timeZone = res.event.timeZone;
                fields.lang = res.event.lang;
            }
            if(fields.timeZone) {
                fields.timeStart = res.event.timeStart;
                fields.timeOpen = res.event.timeOpen;
                fields.timeEnd = res.event.timeEnd;
            }
        }
        if(res?.event?.type === 'area' && fields?.map) {
            const map = getMapAttributes(res.event.map);
            setGlobalState('event.map', map);
        }
        else {
            setGlobalState('event', {...store.getState().event, ...fields});
        }
        return res?.event;
    });
// producer-details-per-event: saves the event-level producer contact fields
// (see ProducerStep.tsx header for the feature overview). contactImage.image arrives
// as base64 from the form — uploaded to S3 here, then the resulting url is sent.
// The wrapXHR key deliberately differs from the request action's own `...XHR` key —
// sharing it makes request() log a false "is blocked" error on every save.
export const updateEventProducer = (fields, eid = getEid()) =>
    wrapXHR('event.updateEventProducerSaveXHR', async () => {
        const body = {...fields};
        if(body.contactImage?.image) {
            const uploadedImage = await uploadFile(body.contactImage.image, '', 'producerLogo', eid);
            // a failed upload must abort the save — sending image:null would DELETE the existing logo
            if(!uploadedImage)
                throw new Error('producer logo upload failed');
            body.contactImage = {...body.contactImage, image: uploadedImage};
        }
        const res = await request('event.updateEventProducer', 'put', `/event/${eid}`, body);
        if(res?.event) {
            const {contactName, contactPhone, contactEmail, contactImage, contactImageId, contactSocialLinks, usePackageContact, packageContact} = res.event;
            setGlobalState('event', {...store.getState().event, contactName, contactPhone, contactEmail, contactImage, contactImageId, contactSocialLinks, usePackageContact, packageContact});
        }
        return res?.event;
    });

// "hide producer contact" toggle state — one flag the server syncs across the sale page
// menu, the sale page contact section and the ticket layout (see the spec: missing = 0 = show)
export const getProducerVisibility = async (eid = getEid()) => {
    const {hideProducerContact, getProducerVisibilityXHR} = store.getState().event;
    // load once per event: reopening the step must not refetch, and a StrictMode
    // double-mount must not fire two parallel calls (the event switch resets the state)
    if(typeof hideProducerContact === 'boolean' || getProducerVisibilityXHR === 'LOADING')
        return {hide: !!hideProducerContact};
    const res = await request('event.getProducerVisibility', 'get', `/event/${eid}/producerVisibility`);
    // request() never throws — a failed call must not fixate a wrong value (and mark it loaded)
    if(res)
        setGlobalState('event.hideProducerContact', !!res.hide);
    return res;
};

export const updateProducerVisibility = async (hide, eid = getEid()) => {
    const res = await request('event.updateProducerVisibility', 'put', `/event/${eid}/producerVisibility`, {hide: hide ? 1 : 0});
    // a failed PUT must not overwrite the real value in the store
    if(res)
        setGlobalState('event.hideProducerContact', !!res.hide);
    return res;
};

// "מענה טלפוני ע"י טיקצ'אק" — quote-request lead (sent to Fix Digital by the server).
// The server stamps event_design.phone_answer_lead_time; a non-zero value means
// "already requested" and the banner shows an indication instead of the CTA.
export const getPhoneAnswerLeadStatus = async (eid = getEid()) => {
    const {phoneAnswerLeadTime, getPhoneAnswerLeadStatusXHR} = store.getState().event;
    // load once per event (same dedupe as getProducerVisibility)
    if(typeof phoneAnswerLeadTime === 'number' || getPhoneAnswerLeadStatusXHR === 'LOADING')
        return {sentTime: phoneAnswerLeadTime || 0};
    const res = await request('event.getPhoneAnswerLeadStatus', 'get', `/event/${eid}/phoneAnswerLead`);
    // request() never throws — a failed call must not fixate 0 (and mark it loaded)
    if(res)
        setGlobalState('event.phoneAnswerLeadTime', res.sentTime || 0);
    return res;
};

export const sendPhoneAnswerLead = async (eid = getEid()) => {
    const res = await request('event.sendPhoneAnswerLead', 'post', `/event/${eid}/phoneAnswerLead`, {});
    if(res?.sentTime)
        setGlobalState('event.phoneAnswerLeadTime', res.sentTime);
    return res;
};

export const createDefaultTemplate = async () => {
    const account = store.getState().account;
    const fields = {pid: account.pid, limit: 1, offset: store.getState().suggestions?.events?.length ?? 0, type: 'allEvents'};
    const res = await request('event.createDefaultTemplate', 'get', `/event/${getEid()}/defaultTemplate`,fields);

    if([EVENT_TYPES.TICKET, EVENT_TYPES.GIVECHAK, EVENT_TYPES.TIMETABLE, EVENT_TYPES.AREA].includes(store.getState().event.type)){
        setGlobalState('event.ready',5);
        setGlobalState('event.previewLastUpdate',getTs());
        setGlobalState('suggestions.events', [...(res?.events || []), ...(store.getState().suggestions?.events ?? [])]);
    }
};

export const getEventSettings = async () => {
    const lang = store.getState().branding?.currentLang || store.getState().event.lang || 'he';
    // the sending popup loads settings before its tabs render, and it also runs at package
    // level (crowd page) where no event is loaded — fall back to the supplied context eid
    const eid = getEid() || store.getState().crm.contextEid;
    let res = await request('event.getEventSettings', 'get', `/event/${eid}/settings`, {lang});
    const {settings,hasSuspendedTransaction}=res;
    for(const key in settings) {
        if(!settings[key])
            settings[key] = {};
    }
    if(!settings.clubs) {
        settings.clubs = {};
    }
    if(!settings.clubs.couponTicketClubs) {
        settings.clubs.couponTicketClubs = [];
    }
    if(!settings.report?.users && settings.report)
        settings.report.users = [];
    if(!settings.report?.transactionUsers)
        settings.report.transactionUsers = [];
    if(settings.blockOrder) {
        if(settings.blockOrder.blockOrders) {
            // const { limit, countLimit, fids, fields, crowd } = settings.blockOrder.blockOrders
            settings.blockOrder = {...settings.blockOrder, ...settings.blockOrder.blockOrders};
            delete settings.blockOrder.blockOrders;
        }
    }
    settings.report.monthlyError = settings.report.monthlyErrorSms || settings.report.monthlyErrorEmail;
    setGlobalState('event.settings', {...store.getState().event.settings, ...settings});
    setGlobalState('event.hasSuspendedTransaction',hasSuspendedTransaction);
};
export const getHalls = async (fields={}) => {
    return await request('event.getHalls', 'get',`/event/${getEid()}/getHalls`,{...fields});
};
// NEW-format seatmaps (the smap library, built in the seatmap studio).
// The endpoints are master-only on the server — non-master sessions get a 403
// and the picker simply shows no NEW maps.
export const getSeatmaps = async () => {
    try {
        const res = await request('event.getSeatmaps', 'get', '/seatmap');
        return res?.data || res?.body?.data || [];
    } catch {
        return [];
    }
};
export const assignSeatmap = async (smid, eid = getEid()) => {
    return await request('event.assignSeatmap', 'post', `/event/${eid}/seatmap`, {smid});
};
export const enableSeatmap = async (on = true, eid = getEid()) => {
    return await request('event.enableSeatmap', 'post', `/seatmap/event/${eid}/enable`, {on});
};
// Management screen (/tickets/seats for NEW maps): everything the panel needs —
// the attached map, its price categories, and the current category→ticket map.
export const getSeatmapState = async (eid = getEid()) => {
    try {
        return await request('event.getSeatmapState', 'get', `/seatmap/event/${eid}/state`);
    } catch {
        return null;
    }
};
// Persist the category→ticket-type attachment. entries: [{categoryId, tid}].
export const saveSeatmapTicketMap = async (entries, eid = getEid()) => {
    return await request('event.saveSeatmapTicketMap', 'put', `/seatmap/event/${eid}/ticket-map`, {entries});
};
// ---- Native seat MANAGER (/tickets/seats for NEW maps) ----
// Full payload: areas, seats (geometry + state + tickets), categories, tickets.
export const getSeatmapManage = async (eid = getEid()) => {
    return await request('event.getSeatmapManage', 'get', `/seatmap/event/${eid}/manage`);
};
// Assign/replace ticket types on seats. seatCodes:[], tids:[], act 'add'|'replace'.
export const setSeatmapSeatTickets = async (seatCodes, tids, act = 'add', eid = getEid()) => {
    return await request('event.setSeatTickets', 'post', `/seatmap/event/${eid}/seats/tickets`, {seatCodes, tids, act});
};
// Remove a single ticket type from seats (server refuses to strip the last one
// with a 409 — handleError lets the manager show the legacy message).
export const removeSeatmapSeatTicket = async (seatCodes, tid, eid = getEid()) => {
    return await request('event.removeSeatTicket', 'delete', `/seatmap/event/${eid}/seats/tickets`, {seatCodes, tid}, {handleError: true});
};
// Edit an area's title plaque (the form-preview pencil): multiline text, move,
// rotate, resize + plaque style (bg/border type + color).
export const setSeatmapAreaLabel = async (smaid, patch, eid = getEid()) => {
    return await request('event.setSeatmapAreaLabel', 'post', `/seatmap/event/${eid}/area-label`, {...(smaid ? {smaid} : {}), ...patch}, {handleError: true});
};
// Bulk variant — ONE document write (the apply-style-to-all button).
export const setSeatmapAreaLabels = async (items, eid = getEid()) => {
    return await request('event.setSeatmapAreaLabels', 'post', `/seatmap/event/${eid}/area-label`, {items}, {handleError: true});
};
// Producer seat state: 'hidden' | 'blocked' | 'free' (release). Never a sold seat.
export const setSeatmapSeatState = async (seatCodes, state, eid = getEid()) => {
    return await request('event.setSeatState', 'post', `/seatmap/event/${eid}/seats/state`, {seatCodes, state});
};
// STANDING areas (capacity pool, no seat codes): attach ticket types to the
// pool ('replace' swaps the whole set; empty tids clears) / set total capacity.
// standing מגבלה: the flagged ticket's own amount IS its per-area limit
export const setSeatmapTicketAmount = async (tid, amount, eid = getEid()) => {
    return await request('event.setTicketAmount', 'put', `/event/${eid}/ticket/${tid}`, {amount});
};

export const setSeatmapAreaTickets = async (areaCodes, tids, act = 'replace', extraTids = [], eid = getEid()) => {
    return await request('event.setAreaTickets', 'post', `/seatmap/event/${eid}/areas/tickets`, {areaCodes, tids, act, extraTids});
};
export const setSeatmapAreaCapacity = async (areaCode, capacity, eid = getEid()) => {
    return await request('event.setAreaCapacity', 'post', `/seatmap/event/${eid}/areas/capacity`, {areaCode, capacity});
};
// תפיסת מקומות אוטומטית — scarcity engine: config + live status (auto-held codes).
export const getSeatmapAuto = async (eid = getEid()) => {
    return await request('event.getSeatmapAuto', 'get', `/seatmap/event/${eid}/auto`);
};
export const saveSeatmapAuto = async (cfg, eid = getEid()) => {
    return await request('event.saveSeatmapAuto', 'post', `/seatmap/event/${eid}/auto`, cfg);
};
// per-event seatmap options (manager side panel) — {allowIsolated} = אפשר להשאיר מושב בודד
export const setSeatmapOptions = async (patch, eid = getEid()) => {
    return await request('event.setSeatmapOptions', 'post', `/seatmap/event/${eid}/options`, patch, {handleError: true});
};
// ---- reservations (שיריונים) ----
export const createSeatmapPartner = async (fields, eid = getEid()) => {
    return await request('event.createPartner', 'post', `/seatmap/event/${eid}/partners`, fields);
};
export const reserveSeatmapSeats = async (seatCodes, smpid, tid, eid = getEid()) => {
    return await request('event.reserveSeats', 'post', `/seatmap/event/${eid}/seats/reserve`, {seatCodes, smpid, tid});
};
export const unreserveSeatmapSeats = async (seatCodes, eid = getEid()) => {
    return await request('event.unreserveSeats', 'post', `/seatmap/event/${eid}/seats/unreserve`, {seatCodes});
};
export const getSuspended = async () => {
    const res =  await request('event.getSuspended', 'get',`/event/${getEid()}/suspended`);
    if(res) {
        setGlobalState('event.settings.suspended', res.suspended);
    }
};
export const sendMap = async (map) => {
    return await request('event.sendMap', 'post',`/event/${getEid()}/sendMap`, {...map});
};
const getSettingsMethod = (settingCategory) => {
    const settings=store.getState().event.settings||{};
    if(['access','blockOrder'].includes(settingCategory)){
        const keys = Object.keys({...(settings.access||{}),...(settings.blockOrder||{})});
        if(keys.length === 0)
            return 'post';
    }
    else if(['payments', 'billings', 'suspended'].includes(settingCategory)) {
        const keys = Object.keys(settings[settingCategory] || {});
        if(keys.length === 0)
            return 'post';
    }
    return 'put';
};

const hasUpdateSettingAccess = (name) => {
    let permission = (name === 'paycash' && 'event cash') || (name === 'hasBillings' && 'billing') || (name === 'hasPayments' && 'payments') || null;
    if(permission)
        return hasAccountAccess(permission);
    return true;
};

const getBlockOrderField = (name, value) => {
    if(['limit', 'countLimit', 'crowd','fieldsCrowd', 'fields', 'fids', 'isMemberLimit'].includes(name)) {
        const {limit, countLimit = 1, fids, fields, crowd, fieldsCrowd, isMemberLimit} = store.getState().event.settings?.blockOrder||{};
        return {
            blockOrders: {
                limit, countLimit, fids, fields, crowd, fieldsCrowd, isMemberLimit,
                [name]: value,
            }
        };
    }
    return {[name]: value};
};
export const updateEventSettings = async (name, value, settingCategory, permission, rolePermission) => {
    const lang = store.getState().branding?.currentLang || store.getState().event.lang || 'he';
    if(!hasUpdateSettingAccess(name))
        return;
    let method = getSettingsMethod(settingCategory);
    let field = {[name]: value};
    if(settingCategory === 'blockOrder')
        field = getBlockOrderField(name, value);
    const args = [`forms.eventSettings.${name}`, method, `/event/${getEid()}/${settingCategory === 'event' ? 'settings' : settingCategory}`,
        {...field, lang}];
    if(permission)
        await highSecurityRequest(permission, rolePermission, ...args);
    else
        await securityRequest(permission,rolePermission, ...args);
    if(name==='customerConfirmationData')
        value = JSON.parse(value);
    if(name==='customerConfirmation' && value)
        setGlobalState('event.settings.suspended.susStatus', value);
    if(name==='whatsAppMessage')
        setGlobalState(`event.settings.${settingCategory}.${name}`, value.active ? 1 : 0);
    else {
        setGlobalState(`event.settings.${settingCategory}.${name}`, value);
    }
    return store.getState().forms.eventSettings[`${name}XHR`];
};

export const updateEventSettingsList = async (name, settingCategory, serverValue, reduxValue) => {
    let field = {[name]: serverValue};
    if(settingCategory === 'blockOrder')
        field = getBlockOrderField(name, serverValue);
    await request(`forms.eventSettings.${name}`, 'put', `/event/${getEid()}/${settingCategory}`,
        field);
    setGlobalState(`event.settings.${settingCategory}.${name}`, reduxValue);
};

export const updateEventDesign = async (name, value, lang, langDesign) => {
    const res = await request('event.updateEventSettings', 'put', `/event/${getEid()}/design`, {[name]: value, lang: lang});
    updateImageState(name, res?.[name], lang, langDesign);
};

export const updateTicketCover = async (data, lang) => {
    lang = lang || store.getState().branding?.currentLang || store.getState().event.lang || 'he';
    const res = await request('forms.eventSettings.ticketCover', 'put', `/event/${getEid()}/ticketCover`, {ticketCover: data, lang});
    setGlobalState('event.settings.design.ticketCover', res?.ticketCover ?? null);
    setGlobalState('event.settings.design.ticketCoverId', res?.ticketCoverId ?? null);
    setGlobalState('event.settings.design.credit.ticketCover', res?.credit ?? null);
    const langDesign = store.getState().event.settings.langDesign || [];
    if(langDesign.length) {
        setGlobalState('event.settings.langDesign', langDesign.map(item =>
            item.lang === lang
                ? {...item, ticketCover: res?.ticketCover ?? null, ticketCoverId: res?.ticketCoverId ?? null, ticketCoverCredit: res?.credit ?? null}
                : item));
    }
};

export const getEventPermissions = async (eid) => {//change name?
    let per = JSON.parse(localStorage.getItem('perEvent_' + eid));
    if(!per) {
        const res = await request('event.getEventPermissions', 'get', `/event/${eid}/permission`);
        if(res.permissions.length) {//??
            per = res.permissions;
            localStorage.setItem('perEvent_' + eid, JSON.stringify(per));
        }
        setGlobalState('event.userEventRole', res.eventRole);//why only hear???
    }
    setGlobalState('event.permissions', per);

};
export const sendEventPromotion = async () => {
    await request('event.sendEventPromotion', 'put', `/event/${getEid()}/promotion`);

};

export const updateEventReady=async (ready=5)=>{
    const res=await request('updateEventReady', 'put', `/event/${getEid()}/ready`,{ready});
    if(res?.ready)
        setGlobalState('event.ready',res.ready);

};
export const publishHubEvent=async ()=>{
    await updateEventReady();
    const {event,account,ui}=store.getState();
    const isAvenueHub=new URLSearchParams(window.location.search).get(AVENUE_QUERY_PARAM)==='1'||ui.event.avenueHubEid===event.eid;
    if(!isAvenueHub||event.ready!==5||account.avenueEid) return;
    updateAvenueEid(event.eid, true).then(res=>{
        if(!res) bounceMessage(i18next.t('package:avenue.setHomeFailed'),'warning');
    });
};
export const getAccounts = async (fields) => {
    const pid = store.getState().account.pid;
    const res = await request('event.getAccounts', 'get', `/package/${pid}/getUserPackages`, {...fields});
    const accounts = store.getState().event?.settings?.multipleAccounts?.accounts || [];
    if(res?.packages)
        setGlobalState('event.settings.multipleAccounts.accounts', [...accounts, ...res.packages]);
};
export const updateMultipleAccount=async (data)=>{
    const accountPid = store.getState().account.pid;
    data.action === 'selectedAll' ? data.accountPid = accountPid : false;
    const res=await request('event.updateMultipleAccount', 'put', `/event/${getEid()}/updateMultipleAccount`,{data});
    if(res){
        setGlobalState('event.settings.multipleAccounts.assignedAccounts', res.multipleAccountsArray);
    }
};

export const updateAutomaticDataAccount=async (data)=>{
    const res=await request('event.updateAutomaticDataAccount', 'put', `/event/${getEid()}/updateAutomaticDataAccount`,{data});
    if(res){
        setGlobalState('event.settings.multipleAccounts.assignedAccounts', res.multipleAccountsArray);
    }
};
export const getAiImages = async ()=>{
    const res = await request('event.getAiImages', 'get', `/event/${getEid()}/aiImages`);
    if(res) {
        setGlobalState('event.aiImages', res.aiImages);
    }

};
export const createSalePageImage = async (details)=>{
    const res = await request(`event.createSalePageImage.${details.style}`, 'post', `/event/${getEid()}/salePageImage`, {details});
    if(res) {
        setGlobalState('event.aiImages',res.aiImages);
    }

};
export const createTopSectionImage = async (details)=>{
    try {
        const res=await request('event.createTopSectionImage', 'post', `/event/${getEid()}/topSectionImage`,{details}, {handleError: true});
        if(res?.ready)
            setGlobalState('event.ready',res.ready);
    } catch (error) {
        setGlobalState('event.ready',4);
    }
};
export const getDescriptionFromGPT = async (descriptionObject)=>{
    const res=await request('event.getDescriptionFromGPT', 'post', `/event/${getEid()}/descriptionFromGPT`,{descriptionObject});
    if(res)
        setGlobalState('event.description',res.description);
    return res.description;
};

export const inactiveEventSendWhatsapp = async ()=>{
    try {
        await request('event.inactiveEventSendWhatsapp', 'post', `/event/${getEid()}/sendWhatsapp`, {handleError: true});
    } catch (error) {
        console.log(error);
    }
};

export const getEventArtistAutomaton = async (text)=>{
    return  await request('event.getArtist', 'get', `/event/${getEid()}/artistAutomaton`,{text});
    // setGlobalState('event.artists', {...store.getState().event.artists, ...result.artists});
};
export const getArtistsBySearch = async (data)=>{
    return await request('event.getArtistsBySearch', 'get', `/event/${getEid()}/artistSearch`,data);
};

export const addArtist = async (data)=>{
    return await request('event.addArtist', 'post', `/event/${getEid()}/newArtist`,data);
};
// export const addArtist = async (artistId)=>{
//     return await request('event.addArtist', 'post', `/event/${getEid()}/artist`,{artistId});
// };
export const addArtists = async (artists)=>{
    await request('event.addArtists', 'put', `/event/${getEid()}/artists`,{artists});
    setGlobalState('event.artists', artists);

};

export const deleteArtist = async (artistId)=>{
    await request('event.deleteArtist', 'delete', `/event/${getEid()}/artist/${artistId}`);
    // const index = store.getState().event.artists.findIndex(a => a.artistId === artistId);
    // unsetGlobalState(`event.artists[${index}]`);
};

export const createDesignImage = async (fields)=>{
    const res=await request('event.createDesignImage', 'post', `/event/${getEid()}/designImage`,fields);
    setGlobalState('event.fbImg',res.image);
};

export const getFbImg = async () => {
    const res = await request('event.getFbImg', 'get', `/event/${getEid()}/fbImg`);
    const fbImg = res?.fbImg ?? res?.image;
    if(fbImg) {
        setGlobalState('event.fbImg', fbImg);
    }
};
export const updateDesignPop=async (showPop= true)=>{
    const res=await request('event.updateDesignPop', 'put', `/event/${getEid()}/designPop`,{showPop});
    setGlobalState('event.showPop', res.showPop);
};
export const publishDesign=async ()=>{
    await request('event.publishDesign', 'put', `/event/${getEid()}/publishDesign`);
};
export const eventDuplication = async (eid, fields, callBack) => {
    let res = await request('event.eventDuplication', 'post', `/event/${eid}/duplicate`, fields);
    setGlobalState('ui.event.duplicateSourceEid', eid);
    callBack(res);
};

export const eventFinishDuplication = async (eid, sourceEid, duplicateEvent) => {
    let res = await request('event.eventDuplication', 'post', `/event/${eid}/finishDuplicate`, {sourceEid ,duplicateEvent});
    if(!res?.newEvent) {
        return false;
    }
    if(res.newEvent.type === 'area') {
        res.newEvent.map = getMapAttributes(res.newEvent.map);
    }
    setGlobalState('event', {...res.newEvent});
    setGlobalState('ui.event.duplicateSourceEid', null);
    return true;
};
export const addGooglePing=async ()=>{
    await request('event.addGooglePing', 'post', `/event/${getEid()}/addGooglePing`);
};

export const updateHidePublishPopup = async (hidePublishPopup) => {
    const res = await request('event.hidePublishPopup', 'put', `/event/${getEid()}/hidePublishPopup`, hidePublishPopup);
    if(res)
        setGlobalState('event.settings.event.hidePublishPopup', res.hidePublishPopup);
};

export const getHidePublishPopup = async () => {
    const res = await request('event.getHidePublishPopup', 'get', `/event/${getEid()}/hidePublishPopup`);
    if(res)
        setGlobalState('event.settings.event.hidePublishPopup', res.hidePublishPopup);
};

export const getImageColors = async () => {
    const res = await request('event.getImageColor', 'get', `/event/${getEid()}/imageColors`);
    if(res?.colors)
        setGlobalState('event.colors', res?.colors);
};

export const postEventReport = async (reportEid, type) => {
    const {pid} = store.getState().account;
    const tickchakToken = readCookie(`tickchak_user_${NODE_ENV}`);
    const link = type === 'excel' ? 'ajax/office/invoice/export_excel_event_summary_multiple':  'ajax/crm/export_pdf_event_summary_multiple';
    let queryString=`tickchakNewApp=1&eids=${reportEid}&pid=${pid}`;
    if(REACT_APP_ENV === 'local'){
        queryString += `&tickchak_token=${tickchakToken}`;
    }
    const res =await request('event.postEventReport', 'post', `${TICK_DOMAIN_MY}/${link}?${queryString}`);
    if(res && res.path)
        downloadURI(res.path,'eventReport');
};

export const getSuspendedQueue = async (eid,tsqid) => {
    return await request('event.getQueue', 'get', `/event/${eid}/suspendedQueue/${tsqid}`);
};

export const getTransactionsDetails = async (eid,tsqid) => {
    const res =  await request('event.transactionsDetails', 'get', `/event/${eid}/transactionsDetails/${tsqid}`);
    if(res?.transactionsReport){
        setGlobalState('event.transactionsReport', res.transactionsReport);
    }
};

export const getPromotionMessage = async (type) => {
    const res = await request('event.getPromotionMessage', 'get', `/event/${getEid()}/promotionMessage`, {type});
    if(res?.message)
        setGlobalState('event.promotionMessage', res.message);
};

export const getPromotionMessageStats = async (range={}) => {
    const res = await request('event.getPromotionMessageStats', 'get', '/promotionMessage/stats', range);
    if(res)
        setGlobalState('event.promotionMessageStats', res);
};

export const getPromotionsStatsByMessage = async (range={}) => {
    const res = await request('event.promotionsStatsByMessage', 'get', '/promotionMessage/statsByMessage', range);
    if(res)
        setGlobalState('event.promotionsStatsByMessage', res);
};

export const stopShowMessage = async (pmid) => {
    await request('event.stopShowMessage', 'post', `/event/${getEid()}/promotionMessage/${pmid}`);
};

export const acceptPromotion = async (pmid) => {
    await request('event.acceptPromotion', 'post', `/event/${getEid()}/acceptPromotion/${pmid}`);
};

export const promotionAnalytics = async (pmid, event, params) => {
    await request('event.promotionAnalytics', 'post', `/event/${getEid()}/promotionMessageAnalytics`, {pmid, event, params});
};

export const approvePromotion = async (pmid, termsApproved) => {
    await request('event.approvePromotion', 'post', `/event/${getEid()}/approvePromotion/${pmid}`, {termsApproved});
    setGlobalState('event.promotionMessage', {...store.getState().event.promotionMessage, termsApproved});
};
export const getGivechakLeads = async(eid, limit = 4, offset = 0) => {
    const res = await request('event.getGivechakLeads', 'get', `/event/${eid}/givechakLeads`, {limit, offset});
    const givechakLeads = store.getState().event.givechakLeads || {};
    if(res){
        if(offset === 0)
            setGlobalState('event.givechakLeads', res);
        else
            setGlobalState('event.givechakLeads.leads', [...givechakLeads.leads, ...res.leads]);
    }
};
const updateImageState = (imageType, value, lang, langDesign) => {
    setGlobalState(`event.settings.design.${imageType}`, value);
    const updateLangDesign = langDesign.map(item => {
        if(item.lang === lang) {
            return {...item, [imageType]: value};
        }
        return item;
    });
    setGlobalState('event.settings.langDesign', updateLangDesign);
    if(imageType === 'fbImg' && langDesign[0].lang == lang)
        setGlobalState('event.fbImg', value);
    if(value?.credit){
        const type = imageType === 'fbImg' ? 'desk' : imageType === 'fbImgMobile' ? 'mobile' : 'post';
        setGlobalState(`event.settings.design.credit.${type}`, value.credit);
    }
};

export const suspendedConfirmation = async (token, additionalData = {}) => {
    try {
        const res = await request('event.suspendedConfirmation', 'post', '/api/suspendedConfirmation', {token, ...additionalData});
        return res;
    } catch (error) {
        return {status: 'failed'};
    }
};

export const chargeSuspendedOrder = async (token) => {
    try {
        const res = await request('event.chargeSuspendedOrder', 'post', '/api/suspendedConfirmation/charge', {token});
        return res;
    } catch (error) {
        return {status: 'willCome_payment_chargeFailed'};
    }
};
export const publishEvent = async (eid, body) => {
    const res = await request('event.publishEvent', 'post', `/event/${eid}/publish`, body);
    if(res?.settings)
        setGlobalState('event.settings.event', {...store.getState().event.settings.event, ...res.settings});
    if(res?.design) {
        if(res.design.fbImgMobile) {
            updateImageState('fbImgMobile', res.design.fbImgMobile, res.lang, store.getState().event.settings.langDesign);
        }
        if(res.design.fbImgPost) {
            updateImageState('fbImgPost', res.design.fbImgPost, res.lang, store.getState().event.settings.langDesign);
        }
    }
};

export const addCoupon = async (couponData, couponTicketClubData, clubName) => {
    const res = await request('event.addCoupon', 'post', `/event/${getEid()}/coupon`, {
        couponData,
        couponTicketClubData
    });
    if(res) {
        const currentCouponTicketClubs = store.getState().event.settings?.clubs?.couponTicketClubs || [];
        const existingIndex = currentCouponTicketClubs.findIndex(item => item.cid === res.cid);

        let updatedCouponTicketClubs;
        if(existingIndex !== -1) {
            updatedCouponTicketClubs = [...currentCouponTicketClubs];
            updatedCouponTicketClubs[existingIndex] = {...res, title: clubName};
        } else {
            updatedCouponTicketClubs = [...currentCouponTicketClubs, {...res, title: clubName}];
        }
        setGlobalState('event.settings.clubs.couponTicketClubs', updatedCouponTicketClubs);
    }
    return res;
};
export const removeCoupon = async (cid) => {
    const res = await request('event.removeCoupon', 'delete', `/event/${getEid()}/coupon/${cid}`);
    if(res) {
        // settings.clubs is already initialized in getEventSettings
        const currentCouponTicketClubs = store.getState().event.settings?.clubs?.couponTicketClubs || [];
        const updatedCouponTicketClubs = currentCouponTicketClubs.map(coupon => {
            if(coupon.cid === cid) {
                return {...coupon, cid: null, clubId: null, ticketToken: ''};
            }
            return coupon;
        });
        setGlobalState('event.settings.clubs.couponTicketClubs', updatedCouponTicketClubs);
    }
};
